import { createDocument, createTheme, p, HeadingLevel } from '@skills/docx-export.jsx';
import { logoBlock } from '@generated/components/logoDocx';
import { cleanLink } from '@generated/components/sceneGuideDocx';
import type { getEventScout } from '@generated/server/eventScout';

type Scout = Awaited<ReturnType<typeof getEventScout>>;
type Ev = Scout['events'][number];

/** Upcoming Events (assignment ideas) as an editable Word file. */
export function buildScoutDocx(s: Scout, paperName: string, title: string) {
  const t = createTheme({ primary: '1F2937', secondary: 'B91C1C', accent: '1D4ED8', neutral: '222222', font: 'Georgia' });
  const prepared = new Date(s.generated).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
  const k: unknown[] = [...logoBlock(), p('Upcoming Events', { heading: HeadingLevel.TITLE }), p(paperName, { italic: true }), p(title, { bold: true }), p(`Prepared ${prepared}`, { size: 16, color: '555555' })];
  if (s.summary) k.push(p(s.summary));
  const item = (e: Ev) => {
    k.push(p(`${e.priority.toUpperCase()} · ${e.paper}`, { size: 14, bold: true, color: 'B91C1C' }));
    k.push(p(e.title, { heading: HeadingLevel.HEADING_2 }));
    k.push(p(`${e.when}${e.where && e.where !== '—' ? ` · ${e.where}` : ''}`, { bold: true }));
    if (e.excerpt) k.push(p(`“${e.excerpt}”`, { italic: true }));
    if (e.whyCover) k.push(p(e.whyCover));
    if (e.angle) k.push(p(`Angle: ${e.angle}`));
    k.push(p(`From ${e.source}${e.link?.startsWith('http') ? ` · ${cleanLink(e.link)}` : ''}`, { size: 16, color: '555555' }));
  };
  k.push(p(`Coming up (${s.events.length})`, { heading: HeadingLevel.HEADING_1 }));
  if (s.events.length) s.events.forEach(item); else k.push(p('No dated upcoming events were listed on these sites.', { italic: true }));
  if (s.ongoing.length) { k.push(p(`Ongoing stories (${s.ongoing.length})`, { heading: HeadingLevel.HEADING_1 })); s.ongoing.forEach(item); }
  k.push(p(`Read: ${s.read.join(', ')}.${s.failed.length ? ` Could not read: ${s.failed.join(', ')}.` : ''}`, { size: 14, color: '777777' }));
  return createDocument({ title: `Upcoming Events · ${title}`, headerText: `${paperName} · Upcoming Events · ${title}`, styles: t.styles, numbering: t.numbering, children: k });
}
